import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { X, Search, ChevronDown, ChevronUp } from 'lucide-react'
import './AssociatedRoles.scss'

/**
 * Associated Roles Component
 *
 * Compact, modern role selector for project creation/editing.
 * Follows the same design pattern as CertificateSelector.
 *
 * Features:
 * - Selected roles displayed as tags at the top with remove option
 * - Collapsible role list
 * - Search/filter functionality
 * - Multi-select with checkboxes
 * - Light-blue highlighting for selected items
 * - Compact, modern UI design
 *
 * @param {Object[]} roles - Array of role objects
 * @param {number[]} selectedRoles - Array of selected role IDs
 * @param {Function} onChange - Callback when selection changes
 * @param {boolean} loading - Loading state
 * @param {boolean} disabled - Disable the selector
 * @param {string} error - Error message to display
 */
const AssociatedRoles = ({
	roles = [],
	selectedRoles = [],
	onChange,
	loading = false,
	disabled = false,
	error = null
}) => {
	const [searchTerm, setSearchTerm] = useState('')
	const [isExpanded, setIsExpanded] = useState(true)

	// Get selected roles with their details
	const selectedRoleDetails = useMemo(() => {
		return roles.filter((role) => selectedRoles.includes(role.id))
	}, [roles, selectedRoles])

	// Filter roles based on search term
	const filteredRoles = useMemo(() => {
		if (!searchTerm.trim()) return roles

		const term = searchTerm.toLowerCase()
		return roles.filter(
			(role) =>
				role.name?.toLowerCase().includes(term) ||
				role.title?.toLowerCase().includes(term) ||
				role.description?.toLowerCase().includes(term) ||
				role.compliance_involvement?.toLowerCase().includes(term)
		)
	}, [roles, searchTerm])

	// Handle checkbox toggle
	const handleToggle = (roleId) => {
		if (disabled) return

		const isSelected = selectedRoles.includes(roleId)
		let newSelectedRoles

		if (isSelected) {
			newSelectedRoles = selectedRoles.filter((id) => id !== roleId)
		} else {
			newSelectedRoles = [...selectedRoles, roleId]
		}

		onChange(newSelectedRoles)
	}

	// Handle removing a selected item (tag)
	const handleRemove = (roleId) => {
		if (disabled) return
		onChange(selectedRoles.filter((id) => id !== roleId))
	}

	// Handle select all visible
	const handleSelectAll = () => {
		if (disabled) return
		const visibleIds = filteredRoles.map((role) => role.id)
		const allSelected = visibleIds.every((id) => selectedRoles.includes(id))

		if (allSelected) {
			// Deselect all visible
			onChange(selectedRoles.filter((id) => !visibleIds.includes(id)))
		} else {
			// Select all visible
			const newValue = [...new Set([...selectedRoles, ...visibleIds])]
			onChange(newValue)
		}
	}

	// Handle clear all
	const handleClearAll = () => {
		if (disabled) return
		onChange([])
	}

	const selectedCount = selectedRoles.length
	const allVisibleSelected = filteredRoles.length > 0 &&
		filteredRoles.every((role) => selectedRoles.includes(role.id))

	// Loading state
	if (loading) {
		return (
			<div className={`role-select ${disabled ? 'role-select--disabled' : ''}`}>
				<div className="role-select__loading">
					<div className="role-select__spinner" />
					<span>Loading roles...</span>
				</div>
			</div>
		)
	}

	// Empty state
	if (roles.length === 0) {
		return (
			<div className={`role-select ${disabled ? 'role-select--disabled' : ''}`}>
				<div className="role-select__empty">No roles available for selection</div>
			</div>
		)
	}

	return (
		<div className={`role-select ${disabled ? 'role-select--disabled' : ''}`}>
			{/* Selected Items as Tags */}
			{selectedCount > 0 && (
				<div className="role-select__selected">
					<div className="role-select__selected-header">
						<span className="role-select__selected-label">
							Selected ({selectedCount})
						</span>
						<button
							type="button"
							className="role-select__clear-all"
							onClick={handleClearAll}
							disabled={disabled}
						>
							Clear All
						</button>
					</div>
					<div className="role-select__tags">
						{selectedRoleDetails.map((role) => (
							<div key={role.id} className="role-select__tag">
								<span className="role-select__tag-text">
									{role.title || role.name}
								</span>
								<button
									type="button"
									className="role-select__tag-remove"
									onClick={() => handleRemove(role.id)}
									disabled={disabled}
									aria-label={`Remove ${role.title || role.name}`}
								>
									<X size={12} />
								</button>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Collapsible List Header */}
			<div className="role-select__header">
				<button
					type="button"
					className="role-select__toggle"
					onClick={() => setIsExpanded(!isExpanded)}
					disabled={loading}
				>
					{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
					<span>
						{isExpanded ? 'Hide' : 'Show'} Available Roles
						{` (${roles.length})`}
					</span>
				</button>
			</div>

			{/* Expandable Content */}
			{isExpanded && (
				<div className="role-select__content">
					{/* Search and Select All Row */}
					<div className="role-select__toolbar">
						<div className="role-select__search">
							<Search size={14} className="role-select__search-icon" />
							<input
								type="text"
								className="role-select__search-input"
								placeholder="Search roles..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								disabled={disabled}
							/>
							{searchTerm && (
								<button
									type="button"
									className="role-select__search-clear"
									onClick={() => setSearchTerm('')}
									disabled={disabled}
								>
									<X size={12} />
								</button>
							)}
						</div>

						{filteredRoles.length > 0 && (
							<button
								type="button"
								className="role-select__select-all"
								onClick={handleSelectAll}
								disabled={disabled}
							>
								{allVisibleSelected ? 'Deselect All' : 'Select All'}
							</button>
						)}
					</div>

					{/* Roles List */}
					<div className="role-select__list">
						{filteredRoles.length === 0 && (
							<div className="role-select__no-results">
								{searchTerm
									? `No roles found for "${searchTerm}"`
									: 'No roles available'}
							</div>
						)}

						{filteredRoles.map((role) => {
							const isSelected = selectedRoles.includes(role.id)
							const displayName = role.title || role.name

							return (
								<label
									key={role.id}
									className={`role-select__item ${isSelected ? 'role-select__item--selected' : ''}`}
								>
									<input
										type="checkbox"
										className="role-select__checkbox"
										checked={isSelected}
										onChange={() => handleToggle(role.id)}
										disabled={disabled}
									/>
									<span className="role-select__item-text">{displayName}</span>
								</label>
							)
						})}
					</div>
				</div>
			)}

			{/* Error Message */}
			{error && <div className="role-select__validation-error">{error}</div>}
		</div>
	)
}

AssociatedRoles.propTypes = {
	roles: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string,
			title: PropTypes.string,
			description: PropTypes.string,
			compliance_involvement: PropTypes.string,
			is_system_default: PropTypes.bool,
		})
	).isRequired,
	selectedRoles: PropTypes.arrayOf(PropTypes.number),
	onChange: PropTypes.func.isRequired,
	loading: PropTypes.bool,
	disabled: PropTypes.bool,
	error: PropTypes.string,
}

export default AssociatedRoles
